import { Box, Typography, Button } from "@mui/material";
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useNavigate } from "react-router";
import { Pages } from "../../utils";
import DashboardHeader from "../../components/dashboard-header";
import { useGetDesignerProjectFiles } from "../../hooks/use-get-designer-project-files";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useUserContext } from "../../hooks/userContext";
import { useGetMoodBoardsQuery } from "../../hooks/use-get-mood-boards-query";
import { useGetBeforeAndAfterProjectsQuery } from "../../hooks/use-get-before-and-after-projects-query";
import { DesignFileBlock } from "./design-file-block";
import { DesignFileListContainer } from "./design-file-list-container";
import { useDesignerOnboardingAccess } from "../../hooks/useDesignerOnboardingAccess";
import { useDeleteProjectFileMutation } from "../../hooks/use-delete-project-file-mutation";
import { useDeleteDesignBoardMutation } from "../../hooks/use-delete-design-board-mutation";
import { useDeleteBeforeAndAfterProjectMutation } from "../../hooks/use-delete-before-and-after-project-mutation";
import { useUpdateProjectFileMutation } from "../../hooks/use-update-project-file-mutation";
import { useUpdateBeforeAndAfterMutation } from "../../hooks/use-update-before-and-after-mutation";
import { useUpdateMoodBoardMutation } from "../../hooks/use-update-mood-board-mutation";
import { useNotification } from "../../contexts/NotificationContext";
import { ProjectPreviewDialog } from "../../components/project-preview-dialog";

type DesignerFileType = "project" | "before-after" | "mood-board";

const horizontalListSx = {
  display: "flex",
  gap: { xs: "12px", md: "20px" },
  overflowX: "auto",
  pb: 2,
  "&::-webkit-scrollbar": {
    height: 6,
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: "#f1f1f1",
    borderRadius: 3,
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#c1c1c1",
    borderRadius: 3,
    "&:hover": {
      backgroundColor: "#a8a8a8",
    },
  },
};

const sortByDisplayOrder = <T,>(files: T[]) => {
  return files
    .map((file, index) => ({ file, index }))
    .sort((a, b) => {
      const aOrder =
        typeof (a.file as any)?.displayOrder === "number"
          ? (a.file as any).displayOrder
          : null;
      const bOrder =
        typeof (b.file as any)?.displayOrder === "number"
          ? (b.file as any).displayOrder
          : null;

      if (aOrder !== null && bOrder !== null) {
        if (aOrder !== bOrder) {
          return aOrder - bOrder;
        }
      } else if (aOrder !== null) {
        return -1;
      } else if (bOrder !== null) {
        return 1;
      }

      return a.index - b.index;
    })
    .map((entry) => entry.file);
};

const SortableFileItem = ({
  id,
  disabled = false,
  children,
}: {
  id: string;
  disabled?: boolean;
  children: ReactNode;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({
      id,
      disabled,
    });

  return (
    <Box
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      sx={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.65 : 1,
        cursor: disabled ? "default" : "grab",
        touchAction: "none",
      }}
    >
      {children}
    </Box>
  );
};

export const MyDesignFilesPage = () => {
  const navigate = useNavigate();
  const { getDesignerProjectFiles, loading, designerProjectFiles } =
    useGetDesignerProjectFiles();
  const {
    getMoodBoards,
    loading: loadingMoodBoards,
    moodBoards,
  } = useGetMoodBoardsQuery();
  const {
    getBeforeAndAfterProjects,
    loading: loadingBeforeAndAfterProjects,
    beforeAndAfterProjects,
  } = useGetBeforeAndAfterProjectsQuery();
  const { deleteProjectFile } = useDeleteProjectFileMutation();
  const { deleteDesignBoard } = useDeleteDesignBoardMutation();
  const { deleteBeforeAndAfterProject } = useDeleteBeforeAndAfterProjectMutation();
  const { updateProjectFile } = useUpdateProjectFileMutation();
  const { updateBeforeAndAfter } = useUpdateBeforeAndAfterMutation();
  const { updateMoodBoard } = useUpdateMoodBoardMutation();
  const { showSuccess, showError } = useNotification();
  const [actionInProgressId, setActionInProgressId] = useState<string | null>(null);
  const [projectFiles, setProjectFiles] = useState<any[]>([]);
  const [beforeAfterFiles, setBeforeAfterFiles] = useState<any[]>([]);
  const [designBoardFiles, setDesignBoardFiles] = useState<any[]>([]);

  // Preview dialog state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFileType, setPreviewFileType] = useState<DesignerFileType | null>(null);
  const [previewFileId, setPreviewFileId] = useState<string | null>(null);

  const { user } = useUserContext();
  const { isDesignerFilesUnlocked } = useDesignerOnboardingAccess();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 150, tolerance: 8 },
    })
  );

  const hasFetched = useRef(false);
  useEffect(() => {
    if (hasFetched.current) return;
    if (!user?.id) {
      return;
    }
    hasFetched.current = true;
    getDesignerProjectFiles(user.id, true);
    getMoodBoards(user.id, true);
    getBeforeAndAfterProjects(user.id, true);
  }, [
    user?.id,
    getDesignerProjectFiles,
    getMoodBoards,
    getBeforeAndAfterProjects,
  ]);

  useEffect(() => {
    setProjectFiles(sortByDisplayOrder(designerProjectFiles ?? []));
  }, [designerProjectFiles]);

  useEffect(() => {
    setBeforeAfterFiles(sortByDisplayOrder(beforeAndAfterProjects ?? []));
  }, [beforeAndAfterProjects]);

  useEffect(() => {
    setDesignBoardFiles(sortByDisplayOrder(moodBoards ?? []));
  }, [moodBoards]);

  const refreshAllFiles = useCallback(async () => {
    if (!user?.id) {
      return;
    }
    await Promise.all([
      getDesignerProjectFiles(user.id, true),
      getMoodBoards(user.id, true),
      getBeforeAndAfterProjects(user.id, true),
    ]);
  }, [user?.id, getDesignerProjectFiles, getMoodBoards, getBeforeAndAfterProjects]);

  const handleDeleteFile = useCallback(
    async (type: DesignerFileType, fileId: string) => {
      if (!fileId) {
        return;
      }

      const noun =
        type === "project"
          ? "project file"
          : type === "before-after"
          ? "before & after project"
          : "design board";

      if (typeof window !== "undefined") {
        const confirmed = window.confirm(
          `Are you sure you want to delete this ${noun}? This action cannot be undone.`
        );
        if (!confirmed) {
          return;
        }
      }

      setActionInProgressId(fileId);

      try {
        let wasDeleted = false;
        if (type === "project") {
          wasDeleted = await deleteProjectFile(fileId);
        } else if (type === "before-after") {
          wasDeleted = await deleteBeforeAndAfterProject(fileId);
        } else {
          wasDeleted = await deleteDesignBoard(fileId);
        }

        if (!wasDeleted) {
          showError("Unable to delete the selected file. Please try again.");
          return;
        }

        showSuccess("File deleted.");
        await refreshAllFiles();
      } catch (error) {
        showError("Delete failed. Please try again.");
      } finally {
        setActionInProgressId(null);
      }
    },
    [
      deleteProjectFile,
      deleteBeforeAndAfterProject,
      deleteDesignBoard,
      showError,
      showSuccess,
      refreshAllFiles,
    ]
  );

  const handlePreviewClick = useCallback((type: DesignerFileType, fileId: string) => {
    setPreviewFileType(type);
    setPreviewFileId(fileId);
    setPreviewOpen(true);
  }, []);

  const handleClosePreview = useCallback(() => {
    setPreviewOpen(false);
    setPreviewFileType(null);
    setPreviewFileId(null);
  }, []);

  const handleToggleFeatured = useCallback(
    async (type: DesignerFileType, file: any) => {
      const fileId = file?._id as string | undefined;
      if (!fileId || !user?.id) {
        return;
      }

      // Only allow Project Files to be added to bio
      if (type !== "project") {
        showError("Only Project Files can be added to bio.");
        return;
      }

      const nextFeatured = !file?.featuredOnBio;
      setActionInProgressId(fileId);

      try {
        await updateProjectFile({
          _id: fileId,
          designerId: user.id,
          featuredOnBio: nextFeatured,
        });

        showSuccess(nextFeatured ? "Added to bio." : "Removed from bio.");
        await refreshAllFiles();
      } catch (error) {
        showError("Could not update featured status. Please try again.");
      } finally {
        setActionInProgressId(null);
      }
    },
    [updateProjectFile, showSuccess, showError, refreshAllFiles, user?.id]
  );

  const handleTogglePublish = useCallback(
    async (type: DesignerFileType, file: any) => {
      const fileId = file?._id as string | undefined;
      if (!fileId || !user?.id) {
        return;
      }

      const nextPublished = !Boolean(file?.published);
      setActionInProgressId(fileId);

      try {
        if (type === "project") {
          await updateProjectFile({
            _id: fileId,
            designerId: user.id,
            published: nextPublished,
          });
        } else if (type === "before-after") {
          await updateBeforeAndAfter({
            _id: fileId,
            designerId: user.id,
            published: nextPublished,
          });
        } else {
          await updateMoodBoard({
            _id: fileId,
            designerId: user.id,
            published: nextPublished,
          });
        }

        showSuccess(nextPublished ? "File published." : "File moved to draft.");
        await refreshAllFiles();
      } catch (error) {
        showError("Could not update publish status. Please try again.");
      } finally {
        setActionInProgressId(null);
      }
    },
    [
      updateProjectFile,
      updateBeforeAndAfter,
      updateMoodBoard,
      showSuccess,
      showError,
      refreshAllFiles,
      user?.id,
    ]
  );

  const persistFileOrder = useCallback(
    async (type: DesignerFileType, orderedFiles: any[]) => {
      if (!user?.id) {
        return;
      }

      try {
        await Promise.all(
          orderedFiles
            .filter((file) => Boolean(file?._id))
            .map((file, index) => {
              const fileId = String(file._id);
              if (type === "project") {
                return updateProjectFile({
                  _id: fileId,
                  designerId: user.id,
                  displayOrder: index,
                });
              }
              if (type === "before-after") {
                return updateBeforeAndAfter({
                  _id: fileId,
                  designerId: user.id,
                  displayOrder: index,
                });
              }
              return updateMoodBoard({
                _id: fileId,
                designerId: user.id,
                displayOrder: index,
              });
            })
        );
      } catch (error) {
        throw error;
      }
    },
    [updateBeforeAndAfter, updateMoodBoard, updateProjectFile, user?.id]
  );

  const handleDragEnd = useCallback(
    async (type: DesignerFileType, event: DragEndEvent) => {
      if (actionInProgressId || !user?.id) {
        return;
      }

      const { active, over } = event;
      if (!over || active.id === over.id) {
        return;
      }

      const activeId = String(active.id);
      const overId = String(over.id);

      const source =
        type === "project"
          ? projectFiles
          : type === "before-after"
          ? beforeAfterFiles
          : designBoardFiles;

      const oldIndex = source.findIndex((file) => String(file?._id) === activeId);
      const newIndex = source.findIndex((file) => String(file?._id) === overId);

      if (oldIndex < 0 || newIndex < 0) {
        return;
      }

      const reordered = arrayMove(source, oldIndex, newIndex);
      if (type === "project") {
        setProjectFiles(reordered);
      } else if (type === "before-after") {
        setBeforeAfterFiles(reordered);
      } else {
        setDesignBoardFiles(reordered);
      }

      setActionInProgressId(`reorder-${type}`);
      try {
        await persistFileOrder(type, reordered);
        showSuccess("Order updated.");
        await refreshAllFiles();
      } catch (error) {
        showError("Could not update file order. Please try again.");
        await refreshAllFiles();
      } finally {
        setActionInProgressId(null);
      }
    },
    [
      actionInProgressId,
      beforeAfterFiles,
      designBoardFiles,
      persistFileOrder,
      projectFiles,
      refreshAllFiles,
      showError,
      showSuccess,
      user?.id,
    ]
  );

  // Helper function to create file cards with placeholders and drag/drop ordering
  const renderFileCards = (files: any[], type: DesignerFileType) => {
    const minFiles = 5;
    const disableActions = Boolean(actionInProgressId);
    const sortableIds = files
      .map((file) => String(file?._id ?? ""))
      .filter(Boolean);

    const fileCards = files
      .map((file) => {
        const fileId = file?._id as string | undefined;
        if (!fileId) {
          return null;
        }

        const imageUrl =
          type === "project"
            ? file?.images?.[0]?.url
            : type === "before-after"
            ? file?.coverImage?.url || file?.afterImage?.url
            : file?.images?.[0]?.url;

        const imageCount =
          type === "project"
            ? file?.images?.length || 0
            : type === "before-after"
            ? (file?.beforeImages?.length || 0) + (file?.afterImages?.length || 0)
            : file?.images?.length || 0;

        const editPath =
          type === "project"
            ? `${Pages.ADD_DESIGNER_FILE__PROJECT_PROFILE}/${fileId}`
            : type === "before-after"
            ? `${Pages.ADD_DESIGNER_FILE__BEFORE_AFTER}/${fileId}`
            : `${Pages.ADD_DESIGNER_FILE__BOARD}/${fileId}`;
        const isPublished = file?.published ?? true;

        return (
          <SortableFileItem key={fileId} id={fileId} disabled={disableActions}>
            <Box
              sx={{
                minWidth: "150px",
                width: "150px",
                flexShrink: 0,
              }}
            >
              <DesignFileBlock
                image={imageUrl ?? ""}
                title={file.name as string}
                lastModified="2 days ago" // TODO: replace with actual updated timestamp when available
                onEdit={() => navigate(editPath)}
                onToggleFeatured={() => handleToggleFeatured(type, file)}
                onTogglePublish={() => handleTogglePublish(type, file)}
                onDelete={() => handleDeleteFile(type, fileId)}
                onPreview={() => handlePreviewClick(type, fileId)}
                isFeatured={Boolean(file?.featuredOnBio)}
                published={isPublished}
                disabled={disableActions}
                fileType={type}
                imageCount={imageCount}
              />
            </Box>
          </SortableFileItem>
        );
      })
      .filter(Boolean);

    const placeholderCards: ReactNode[] = [];
    if (files.length < minFiles) {
      const placeholderCount = minFiles - files.length;
      for (let i = 0; i < placeholderCount; i++) {
        const addPath =
          type === "project"
            ? Pages.ADD_DESIGNER_FILE__PROJECT_PROFILE
            : type === "before-after"
            ? Pages.ADD_DESIGNER_FILE__BEFORE_AFTER
            : Pages.ADD_DESIGNER_FILE__BOARD;

        placeholderCards.push(
          <Box
            key={`placeholder-${type}-${i}`}
            sx={{
              minWidth: "150px",
              width: "150px",
              flexShrink: 0,
            }}
          >
            <DesignFileBlock image="" title="" to={addPath} isPlaceholder={true} />
          </Box>
        );
      }
    }

    return (
      <Box sx={horizontalListSx}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={(event) => {
            void handleDragEnd(type, event);
          }}
        >
          <SortableContext items={sortableIds} strategy={horizontalListSortingStrategy}>
            {fileCards}
          </SortableContext>
        </DndContext>
        {placeholderCards}
      </Box>
    );
  };

  // Route guard: Show message if designer files is locked instead of redirecting
  if (user?.accountType === "Designer" && !isDesignerFilesUnlocked) {
    return (
      <Box
        sx={{
          backgroundColor: "#F5F5F5",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            backgroundColor: "#fff",
            borderRadius: "16px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
            p: 4,
            maxWidth: "500px",
            textAlign: "center",
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
            Complete Your Bio First
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary", mb: 3 }}>
            You need to complete your Designer Bio before you can access Designer Files.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate(Pages.MY_BIO)}
            sx={{ backgroundColor: "#00A06D" }}
          >
            Go to My Bio
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: "#F5F5F5",
        minHeight: "100vh",
      }}
    >
      <DashboardHeader
        title="Design Files"
        showBackButton={false}
        secondaryAction={{
          label: "View All Files",
          onClick: () =>
            navigate(`${Pages.DESIGNER_FILES_PROJECTS}?filter=all&clear=1`),
          variant: "outlined",
        }}
        primaryAction={{
          label: "Add Designer File",
          onClick: () => navigate(Pages.ADD_DESIGNER_FILE),
          variant: "contained",
        }}
      />

      <DesignFileListContainer
        loading={loading}
        title="Project Files"
        description="Please upload a minimum of 5 project files. Drag and drop to reorder. Select which Designer Files you want featured in your bio, to be shared with your client matches."
        count={projectFiles.length}
        showViewAll
        viewAllPath={`${Pages.DESIGNER_FILES_PROJECTS}?filter=project&clear=1`}
      >
        {renderFileCards(projectFiles, "project")}
      </DesignFileListContainer>

      <DesignFileListContainer
        title="Before & Afters"
        loading={loadingBeforeAndAfterProjects}
        count={beforeAfterFiles.length}
        showViewAll
        viewAllPath={`${Pages.DESIGNER_FILES_PROJECTS}?filter=before-after&clear=1`}
      >
        {renderFileCards(beforeAfterFiles, "before-after")}
      </DesignFileListContainer>

      <DesignFileListContainer
        title="Design Boards"
        loading={loadingMoodBoards}
        count={designBoardFiles.length}
        showViewAll
        viewAllPath={`${Pages.DESIGNER_FILES_PROJECTS}?filter=design-board&clear=1`}
      >
        {renderFileCards(designBoardFiles, "mood-board")}
      </DesignFileListContainer>

      {/* Preview Dialog */}
      {previewFileType && (
        <ProjectPreviewDialog
          open={previewOpen}
          onClose={handleClosePreview}
          fileType={previewFileType}
          fileId={previewFileId}
        />
      )}
    </Box>
  );
};
